import AutoAwesomeIcon from '@material-design-icons/svg/filled/auto_awesome.svg?react';
import MailIcon from '@material-design-icons/svg/filled/mail.svg?react';
import PlayIcon from '@material-design-icons/svg/filled/play_arrow.svg?react';
import RefreshIcon from '@material-design-icons/svg/filled/refresh.svg?react';
import ScheduleIcon from '@material-design-icons/svg/filled/schedule.svg?react';
import { isLeft, isRight } from 'fp-ts/Either';
import { use, useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { TRPCContext } from '../contexts/TRPC';
import {
  FieldLabel,
  SectionCard,
  SectionHeader,
  StatusInline,
  StatusPill,
  fieldInputClass,
} from './aiSummaries/primitives';
import {
  formatDateTime,
  formatRelative,
  getRunStatusVariant,
  formatRunStatus,
} from './aiSummaries/runFormatting';
import { Button } from './aiSummaries/uiButton';
import { SearchableMultiSelect } from './SearchableMultiSelect';

interface SettingsForm {
  enabled: boolean;
  publisherEmailEnabled: boolean;
  lookbackDays: number;
  scheduleDayOfWeekUtc: number;
  scheduleHourUtc: number;
  sonnetModel: string;
  opusModel: string;
  appConcurrency: number;
  maxReviewPagesPerApp: number;
  maxReviewsPerApp: number;
}

interface LatestRun {
  status: string;
  windowStart: Date | string;
  windowEnd: Date | string;
  appsScanned: number;
  appsWithStoreListing: number;
  appsWithRecentTextReviews: number;
  reviewsFetched: number;
  reviewsIncluded: number;
  summariesWritten: number;
  appErrors: number;
  summaryEmailsSent: number;
  summaryEmailsSkipped: number;
  summaryEmailFailures: number;
  errorMessage: string | null;
  startedAt: Date | string;
  completedAt: Date | string | null;
}

interface SummaryStatus {
  settings: SettingsForm & {
    anthropicConfigured: boolean;
  };
  latestRun: LatestRun | null;
  latestStoredWindow: {
    windowStart: Date | string;
    windowEnd: Date | string;
    status: string;
    summaryCount: number;
  } | null;
}

interface SummaryDappOption {
  id: string;
  dappName: string;
  androidPackage: string;
}

type ManualEmailMode = 'None' | 'Override' | 'Publisher';

const DEFAULT_FORM: SettingsForm = {
  enabled: false,
  publisherEmailEnabled: false,
  lookbackDays: 7,
  scheduleDayOfWeekUtc: 1,
  scheduleHourUtc: 9,
  sonnetModel: 'claude-sonnet-4-6',
  opusModel: 'claude-opus-4-7',
  appConcurrency: 2,
  maxReviewPagesPerApp: 50,
  maxReviewsPerApp: 500,
};

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => hour);
const WEEKDAY_OPTIONS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

function parseIntegerInput(value: string, fallback: number) {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function getManualRunToastMessage(selectedAppCount: number): string {
  if (selectedAppCount === 0) {
    return 'Run started for all apps';
  }

  if (selectedAppCount === 1) {
    return 'Run started for 1 selected app';
  }

  return `Run started for ${selectedAppCount} selected apps`;
}

function getManualRunToastDescription(
  manualEmailMode: ManualEmailMode,
  manualEmailOverride: string,
): string {
  if (manualEmailMode === 'None') {
    return 'No emails will be sent for this manual run.';
  }

  if (manualEmailMode === 'Override') {
    return `Eligible summaries will email ${manualEmailOverride.trim()}.`;
  }

  return 'Eligible summaries may email publishers with weekly limits enforced.';
}

export function DappReviewSummarySettingsPanel() {
  const trpc = use(TRPCContext);
  const [form, setForm] = useState<SettingsForm>(DEFAULT_FORM);
  const [status, setStatus] = useState<SummaryStatus | null>(null);
  const [dappOptions, setDappOptions] = useState<SummaryDappOption[]>([]);
  const [selectedAndroidPackages, setSelectedAndroidPackages] = useState<
    string[]
  >([]);
  const [manualEmailMode, setManualEmailMode] =
    useState<ManualEmailMode>('None');
  const [manualEmailOverride, setManualEmailOverride] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingDapps, setLoadingDapps] = useState(true);
  const [saving, setSaving] = useState(false);
  const [triggeringRun, setTriggeringRun] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      setLoading(true);
      const result =
        await trpc.client.getDappReviewSummaryStatus.query(undefined);

      if (isRight(result)) {
        setStatus(result.right as SummaryStatus);
        setForm({
          enabled: result.right.settings.enabled,
          publisherEmailEnabled: result.right.settings.publisherEmailEnabled,
          lookbackDays: result.right.settings.lookbackDays,
          scheduleDayOfWeekUtc: result.right.settings.scheduleDayOfWeekUtc,
          scheduleHourUtc: result.right.settings.scheduleHourUtc,
          sonnetModel: result.right.settings.sonnetModel,
          opusModel: result.right.settings.opusModel,
          appConcurrency: result.right.settings.appConcurrency,
          maxReviewPagesPerApp: result.right.settings.maxReviewPagesPerApp,
          maxReviewsPerApp: result.right.settings.maxReviewsPerApp,
        });
      } else if (isLeft(result)) {
        toast.error(result.left.message);
      }
    } catch {
      toast.error('Failed to load dapp review summary settings');
    } finally {
      setLoading(false);
    }
  }, [trpc.client]);

  const loadDapps = useCallback(async () => {
    try {
      setLoadingDapps(true);
      const result =
        await trpc.client.listDappReviewSummaryDapps.query(undefined);

      if (isRight(result)) {
        setDappOptions(result.right as SummaryDappOption[]);
        const availablePackages = new Set(
          result.right.map((dapp) => dapp.androidPackage),
        );
        setSelectedAndroidPackages((current) =>
          current.filter((androidPackage) =>
            availablePackages.has(androidPackage),
          ),
        );
      } else if (isLeft(result)) {
        toast.error(result.left.message);
      }
    } catch {
      toast.error('Failed to load dapp review summary targets');
    } finally {
      setLoadingDapps(false);
    }
  }, [trpc.client]);

  useEffect(() => {
    void loadStatus();
    void loadDapps();
  }, [loadDapps, loadStatus]);

  const dappChoices = dappOptions.map((dapp) => ({
    key: dapp.androidPackage,
    label: `${dapp.dappName} (${dapp.androidPackage})`,
  }));

  const updateNumber = (field: keyof SettingsForm, value: string) => {
    setForm((current) => ({
      ...current,
      [field]: parseIntegerInput(value, current[field] as number),
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const result =
        await trpc.client.updateDappReviewSummarySettings.mutate(form);

      if (isRight(result)) {
        toast.success('Settings saved');
        await loadStatus();
      } else if (isLeft(result)) {
        toast.error(result.left.message);
      }
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTriggerRun = async () => {
    if (manualEmailMode === 'Override' && !manualEmailOverride.trim()) {
      toast.error('Enter an override email before starting the run');
      return;
    }

    let confirmPublisherEmail = false;
    if (manualEmailMode === 'Override') {
      const confirmed = window.confirm(
        `This manual run will email eligible summaries to ${manualEmailOverride.trim()}. If this address belongs to an app publisher, this confirmation is required and the weekly publisher limit will be enforced. Continue?`,
      );
      if (!confirmed) {
        return;
      }
      confirmPublisherEmail = true;
    }

    if (manualEmailMode === 'Publisher') {
      const confirmed = window.confirm(
        'This manual run can email app publishers for eligible summaries. Apps with a publisher summary email sent in the last 7 days will be skipped. Continue?',
      );
      if (!confirmed) {
        return;
      }
      confirmPublisherEmail = true;
    }

    try {
      setTriggeringRun(true);

      const result = await trpc.client.triggerDappReviewSummaryRun.mutate({
        force: true,
        androidPackages: selectedAndroidPackages,
        emailMode: manualEmailMode,
        emailOverride:
          manualEmailMode === 'Override' ? manualEmailOverride.trim() : null,
        confirmPublisherEmail,
      });

      if (isRight(result)) {
        toast.success(
          getManualRunToastMessage(selectedAndroidPackages.length),
          {
            description: getManualRunToastDescription(
              manualEmailMode,
              manualEmailOverride,
            ),
          },
        );
        await loadStatus();
        window.setTimeout(() => void loadStatus(), 3000);
      } else if (isLeft(result)) {
        toast.error(result.left.message);
      }
    } catch {
      toast.error('Failed to start run');
    } finally {
      setTriggeringRun(false);
    }
  };

  const latestRun = status?.latestRun;
  const canTriggerRun = Boolean(status?.settings.anthropicConfigured);
  const triggerLabel =
    selectedAndroidPackages.length === 0
      ? 'Run all apps'
      : `Run ${selectedAndroidPackages.length} selected`;
  const scheduleWeekday =
    WEEKDAY_OPTIONS[form.scheduleDayOfWeekUtc] ??
    WEEKDAY_OPTIONS[DEFAULT_FORM.scheduleDayOfWeekUtc];
  const scheduleLabel = `${scheduleWeekday} ${String(
    form.scheduleHourUtc,
  ).padStart(2, '0')}:00 UTC · ${form.lookbackDays}d lookback`;
  const latestRunVariant = latestRun
    ? getRunStatusVariant(latestRun.status)
    : 'neutral';

  return (
    <SectionCard>
      <SectionHeader
        eyebrow="Configuration"
        title="Schedule & run controls"
        actions={
          <Button
            variant="secondary"
            size="md"
            onClick={handleSave}
            disabled={loading || saving}
            submitting={saving}
          >
            Save settings
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-white/[0.06] px-6 py-3.5 text-[13px]">
        <StatusInline
          variant={status?.settings.anthropicConfigured ? 'success' : 'error'}
        >
          <span className="text-neutral-500">Anthropic key</span>
          <span className="font-medium text-neutral-200">
            {status?.settings.anthropicConfigured ? 'Configured' : 'Missing'}
          </span>
        </StatusInline>

        <span className="hidden h-3 w-px bg-white/10 md:inline-block" />

        <StatusInline
          variant={form.publisherEmailEnabled ? 'warning' : 'neutral'}
        >
          <MailIcon className="size-3.5 fill-neutral-500" />
          <span className="text-neutral-500">Publisher email</span>
          <span className="font-medium text-neutral-200">
            {form.publisherEmailEnabled ? 'Enabled' : 'Disabled'}
          </span>
        </StatusInline>

        <span className="hidden h-3 w-px bg-white/10 md:inline-block" />

        <span className="inline-flex items-center gap-2 text-[13px] text-neutral-300">
          <ScheduleIcon className="size-3.5 fill-neutral-500" />
          <span className="text-neutral-500">Schedule</span>
          <span className="font-medium text-neutral-200">{scheduleLabel}</span>
        </span>

        <span className="hidden h-3 w-px bg-white/10 md:inline-block" />

        <span className="inline-flex items-center gap-2 text-[13px]">
          <span className="text-neutral-500">Latest run</span>
          {latestRun ? (
            <StatusPill variant={latestRunVariant}>
              {formatRunStatus(latestRun.status)}
            </StatusPill>
          ) : (
            <span className="font-medium text-neutral-500">No runs yet</span>
          )}
          {latestRun && (
            <span className="text-neutral-500">
              · {formatRelative(latestRun.startedAt) ?? 'unknown'}
            </span>
          )}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-x-10 gap-y-8 p-6 lg:grid-cols-2">
        <FormGroup title="Schedule">
          <ToggleField
            label="Enabled"
            description="Skips scheduled runs. Manual triggers still work."
            checked={form.enabled}
            disabled={loading || saving}
            onChange={(value) =>
              setForm((current) => ({ ...current, enabled: value }))
            }
          />

          <ToggleField
            label="Scheduled publisher emails"
            description="Disabled by default. Scheduled runs send only eligible summaries and enforce one publisher email per app every 7 days."
            checked={form.publisherEmailEnabled}
            disabled={loading || saving}
            onChange={(value) =>
              setForm((current) => ({
                ...current,
                publisherEmailEnabled: value,
              }))
            }
          />

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <label className="space-y-1.5">
              <FieldLabel hint="days">Lookback</FieldLabel>
              <input
                type="number"
                aria-label="Lookback"
                min={1}
                max={30}
                value={form.lookbackDays}
                disabled={loading || saving}
                onChange={(event) =>
                  updateNumber('lookbackDays', event.target.value)
                }
                className={fieldInputClass}
              />
            </label>

            <label className="space-y-1.5">
              <FieldLabel hint="UTC">Run on</FieldLabel>
              <select
                value={form.scheduleDayOfWeekUtc}
                disabled={loading || saving}
                onChange={(event) =>
                  updateNumber('scheduleDayOfWeekUtc', event.target.value)
                }
                className={fieldInputClass}
              >
                {WEEKDAY_OPTIONS.map((weekday, index) => (
                  <option key={weekday} value={index}>
                    {weekday}
                  </option>
                ))}
              </select>
            </label>

            <label className="space-y-1.5">
              <FieldLabel hint="UTC">Run at</FieldLabel>
              <select
                value={form.scheduleHourUtc}
                disabled={loading || saving}
                onChange={(event) =>
                  updateNumber('scheduleHourUtc', event.target.value)
                }
                className={fieldInputClass}
              >
                {HOUR_OPTIONS.map((hour) => (
                  <option key={hour} value={hour}>
                    {String(hour).padStart(2, '0')}:00
                  </option>
                ))}
              </select>
            </label>
          </div>
        </FormGroup>

        <FormGroup title="Models">
          <label className="space-y-1.5">
            <FieldLabel hint="classification">Sonnet</FieldLabel>
            <input
              type="text"
              aria-label="Sonnet"
              value={form.sonnetModel}
              disabled={loading || saving}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  sonnetModel: event.target.value,
                }))
              }
              className={fieldInputClass}
            />
          </label>

          <label className="space-y-1.5">
            <FieldLabel hint="summarization">Opus</FieldLabel>
            <input
              type="text"
              aria-label="Opus"
              value={form.opusModel}
              disabled={loading || saving}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  opusModel: event.target.value,
                }))
              }
              className={fieldInputClass}
            />
          </label>
        </FormGroup>

        <FormGroup title="Limits">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <label className="space-y-1.5">
              <FieldLabel hint="apps">Concurrency</FieldLabel>
              <input
                type="number"
                aria-label="Concurrency"
                min={1}
                max={5}
                value={form.appConcurrency}
                disabled={loading || saving}
                onChange={(event) =>
                  updateNumber('appConcurrency', event.target.value)
                }
                className={fieldInputClass}
              />
            </label>
            <label className="space-y-1.5">
              <FieldLabel hint="per app">Pages</FieldLabel>
              <input
                type="number"
                aria-label="Pages"
                min={1}
                max={200}
                value={form.maxReviewPagesPerApp}
                disabled={loading || saving}
                onChange={(event) =>
                  updateNumber('maxReviewPagesPerApp', event.target.value)
                }
                className={fieldInputClass}
              />
            </label>
            <label className="space-y-1.5">
              <FieldLabel hint="per app">Reviews</FieldLabel>
              <input
                type="number"
                aria-label="Reviews"
                min={10}
                max={2000}
                value={form.maxReviewsPerApp}
                disabled={loading || saving}
                onChange={(event) =>
                  updateNumber('maxReviewsPerApp', event.target.value)
                }
                className={fieldInputClass}
              />
            </label>
          </div>
        </FormGroup>

        <FormGroup
          title="Manual run"
          description={
            selectedAndroidPackages.length === 0
              ? 'Leave empty to run all apps.'
              : `${selectedAndroidPackages.length} selected.`
          }
          actions={
            <span className="text-[11px] text-neutral-500">
              {dappOptions.length} available
            </span>
          }
        >
          <SearchableMultiSelect
            choices={dappChoices}
            value={selectedAndroidPackages}
            placeholder={
              loadingDapps ? 'Loading apps…' : 'Select apps, or leave empty'
            }
            searchPlaceholder="Search published apps…"
            maxVisibleSelections={6}
            disabled={loadingDapps || triggeringRun}
            onChange={setSelectedAndroidPackages}
          />

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="space-y-1.5">
              <FieldLabel hint="manual only">Email</FieldLabel>
              <select
                value={manualEmailMode}
                disabled={loading || triggeringRun}
                onChange={(event) =>
                  setManualEmailMode(
                    event.target.value as 'None' | 'Override' | 'Publisher',
                  )
                }
                className={fieldInputClass}
              >
                <option value="None">Do not send email</option>
                <option value="Override">Send to override email</option>
                <option value="Publisher">Send to publisher</option>
              </select>
            </label>

            {manualEmailMode === 'Override' && (
              <label className="space-y-1.5">
                <FieldLabel hint="recipient">Override email</FieldLabel>
                <input
                  type="email"
                  aria-label="Override email"
                  value={manualEmailOverride}
                  disabled={loading || triggeringRun}
                  onChange={(event) =>
                    setManualEmailOverride(event.target.value)
                  }
                  placeholder="name@example.com"
                  className={fieldInputClass}
                />
              </label>
            )}
          </div>

          <Button
            variant="secondary"
            size="md"
            onClick={handleTriggerRun}
            disabled={loading || triggeringRun || !canTriggerRun}
            submitting={triggeringRun}
            className="w-full md:w-auto"
          >
            {!triggeringRun &&
              (canTriggerRun ? (
                <PlayIcon className="size-4 fill-current" />
              ) : (
                <AutoAwesomeIcon className="size-4 fill-current" />
              ))}
            {canTriggerRun ? triggerLabel : 'Anthropic key required'}
          </Button>
        </FormGroup>
      </div>

      {latestRun && <LatestRunStrip run={latestRun} />}
    </SectionCard>
  );
}

function FormGroup({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-[13px] font-semibold text-white tracking-tight">
            {title}
          </h3>
          {description && (
            <p className="mt-1 text-[12px] leading-relaxed text-neutral-500">
              {description}
            </p>
          )}
        </div>
        {actions}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function ToggleField({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-4 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3.5 py-3">
      <div className="min-w-0">
        <div className="text-[13px] font-medium text-white">{label}</div>
        {description && (
          <p className="mt-0.5 text-[12px] leading-snug text-neutral-500">
            {description}
          </p>
        )}
      </div>
      <span
        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
          checked ? 'bg-teal-500/80' : 'bg-white/10'
        } ${disabled ? 'opacity-50' : ''}`}
      >
        <input
          type="checkbox"
          aria-label={label}
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
          className="sr-only"
        />
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        />
      </span>
    </label>
  );
}

function LatestRunStrip({ run }: { run: LatestRun }) {
  const summaryEmailsSent = run.summaryEmailsSent ?? 0;
  const summaryEmailsSkipped = run.summaryEmailsSkipped ?? 0;
  const summaryEmailFailures = run.summaryEmailFailures ?? 0;
  const stats: Array<{ label: string; value: string; tone?: 'error' }> = [
    {
      label: 'Started',
      value: formatRelative(run.startedAt) ?? formatDateTime(run.startedAt),
    },
    {
      label: 'Completed',
      value: run.completedAt
        ? (formatRelative(run.completedAt) ?? formatDateTime(run.completedAt))
        : '—',
    },
    { label: 'Apps scanned', value: String(run.appsScanned) },
    { label: 'Store listings', value: String(run.appsWithStoreListing) },
    { label: 'Reviews in', value: String(run.reviewsIncluded) },
    { label: 'Summaries', value: String(run.summariesWritten) },
    {
      label: 'Emails',
      value: `${summaryEmailsSent}/${summaryEmailsSkipped + summaryEmailFailures}`,
      tone: summaryEmailFailures > 0 ? 'error' : undefined,
    },
    {
      label: 'Errors',
      value: String(run.appErrors),
      tone: run.appErrors > 0 ? 'error' : undefined,
    },
  ];

  return (
    <div className="border-t border-white/[0.06]">
      <div className="flex items-center gap-2 px-6 pb-2 pt-4 text-[11px] uppercase tracking-[0.14em] text-neutral-500">
        <RefreshIcon className="size-3 fill-current" />
        Latest run
      </div>
      <div className="grid grid-cols-2 divide-x divide-y divide-white/[0.05] border-t border-white/[0.05] md:grid-cols-4 lg:grid-cols-8">
        {stats.map((stat) => (
          <div key={stat.label} className="px-5 py-4">
            <div className="text-[11px] uppercase tracking-[0.12em] text-neutral-500">
              {stat.label}
            </div>
            <div
              className={`mt-1 text-sm font-medium ${
                stat.tone === 'error' ? 'text-rose-300' : 'text-white'
              }`}
            >
              {stat.value}
            </div>
          </div>
        ))}
      </div>

      {run.errorMessage && (
        <div className="mx-6 my-4 rounded-lg border border-rose-400/20 bg-rose-400/10 px-3 py-2 text-sm text-rose-200">
          {run.errorMessage}
        </div>
      )}
    </div>
  );
}
